import { yupResolver } from "@hookform/resolvers/yup"
import { Alert, Button, Checkbox, Link, Modal, Text, Toast, useToast } from "@oysterhr/ui"
import { useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import * as Yup from "yup"
import { companySepaMandatePath } from "@oysterhr/router"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { PaymentMethodsCustomerBankAccountSepaType } from "@oysterhr/schema"
import { useConfirmMandateLodging } from "../sepa/hooks/useConfirmMandateLodging"

interface ConfirmMandateSharingModalProps {
  isOpen: boolean
  customerBankAccount: PaymentMethodsCustomerBankAccountSepaType
  onClose: () => void
}

export const ConfirmMandateSharingModal = ({
  isOpen,
  customerBankAccount,
  onClose,
}: ConfirmMandateSharingModalProps) => {
  const { accountAspect: companyId } = useAccountAspect()
  const toast = useToast()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "confirmMandateModal",
  })
  const [lodgeMandate, { loading: isConfirmingLodging }] = useConfirmMandateLodging()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      checked: false,
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = () => {
    const mandateId = customerBankAccount.mandate?.id
    if (!mandateId) throw new Error("No mandate found for this bank account")
    lodgeMandate({
      variables: { mandateId: mandateId },
      onCompleted: data => {
        if (data.confirmMandateLodging.errors.length) {
          toast({
            render: (
              <Toast variation="error">
                <Toast.Content>{data.confirmMandateLodging.errors.join(", ")}</Toast.Content>
              </Toast>
            ),
          })
        } else {
          onClose()
        }
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header align="center">
          <div className="flex flex-col items-center justify-center">
            <img src="/images/payments/signing.png" alt="Confirm mandate sharing" />
            {t("title")}
          </div>
        </Modal.Header>
        <Modal.Content>
          <Text className="mt-4 text-center">
            <Trans
              t={t}
              i18nKey="content"
              components={{
                a: (
                  <Link
                    to={companySepaMandatePath(companyId)}
                    className="inline-block"
                    target="_blank"
                  />
                ),
              }}
            />
          </Text>
          <Alert variation="warning" className="my-4">
            <Alert.Icon />
            <Alert.Content> {t("alert")}</Alert.Content>
          </Alert>
          <div className="rounded-lg border border-gray-200 p-4">
            <Checkbox
              {...register("checked")}
              label={t("label")}
              hasError={Boolean(errors.checked)}
            />
          </div>
          {errors.checked?.message && (
            <Text className="mt-1 text-error-base" size="small">
              {errors.checked?.message}
            </Text>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button variation="outline" onClick={onClose}>
            {t("btnCancel")}
          </Button>
          <Button type="submit" isLoading={isConfirmingLodging} disabled={isConfirmingLodging}>
            {t("btnConfirm")}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

const validationSchema = Yup.object().shape({
  checked: Yup.boolean().oneOf([true], "Confirm you’ve shared the mandate to continue"),
})
